import axiosClient from 'api/axiosClient';
import useGroupChats from '../../store/conversations/useGroupChats';
import { useCallback } from 'react';

// Module scope, not a ref: several components call this hook, and every instance writes
// to the same store, so the "is this response still the current one" question has to be
// answered across all of them rather than per instance.
let latestGroupChatsRequestId = 0;

/**
 * Group Chats hooks
 */
export default function useGroupChatsHooks() {
  const { setState } = useGroupChats();

  const formatPayload = useCallback(
    (searchTextFilterValue: string, selectedGroups: any[], pageNumber: number, pageLimit: number) => {
      // Read non-reactively at call time (not a positional arg or a closed-over value) so
      // every fetch path -- the search debounce, pagination, page-limit, tab switch --
      // carries the active saved filter with its current value, even from a stale closure.
      const { savedFilter } = useGroupChats.getState().teamInboxFilters;

      return {
        search: searchTextFilterValue || '',
        ...(savedFilter ? { savedFilterId: savedFilter._id } : {}),
        page: pageNumber,
        limit: pageLimit,
        groupIds: selectedGroups?.length ? selectedGroups.map((item: any) => item.value) : undefined,
      };
    },
    []
  );

  const fetchGroupChats = async (payload: any) => {
    // Filter changes, the search debounce and pagination can all have a request in
    // flight at once. Only the newest may write: without this a slow failure landing
    // after a fast success left `error` true over a freshly loaded list, hiding it
    // behind the alert, and cleared conversationList out from under it.
    const requestId = ++latestGroupChatsRequestId;
    const isSuperseded = () => requestId !== latestGroupChatsRequestId;

    setState({ error: false, isLoading: true });
    try {
      const data = await axiosClient.post('/conversations/team-inbox', payload).then(res => res.data);
      if (isSuperseded()) return;

      if (!data?.error) {
        const { data: list, page, limit, totalCount } = data;
        setState({
          conversationList: list,
          pagination: {
            page: page ?? 1,
            limit: limit ?? 50,
            total: totalCount,
            pages: Math.ceil(totalCount / limit),
          },
          isLoading: false,
          // Not just at request start: a success has to be able to clear a failure.
          error: false,
        });

        return;
      }
    } catch (error: any) {
      if (isSuperseded()) return;

      // A saved filter the backend rejects -- deleted in another tab (404) or malformed
      // (400) -- would otherwise stay selected and sink every later request: each search
      // keystroke and page change re-sends the dead id and fails the same silent way.
      // Drop it so the effect watching it refetches unfiltered, and so the dropdown label
      // reverts, which is the admin's signal that the filter is gone. This cannot loop:
      // the retry carries no savedFilterId, so it can never re-enter this branch.
      const status = error?.response?.status;
      const savedFilterRejected = Boolean(payload?.savedFilterId) && (status === 400 || status === 404);

      if (savedFilterRejected) {
        useGroupChats.getState().setSavedFilter(null);
      }

      // error must be true, not false: the list previously rendered its ordinary empty
      // state on failure, so a broken request was indistinguishable from an empty inbox.
      // isLoading must be cleared too or the list stays stuck on skeletons.
      setState({ error: true, isLoading: false, conversationList: [] });
    }
  };

  const fetchGroupChatById = async (chatId: string) => {
    try {
      const data = await axiosClient.get(`/conversations/team-inbox/${chatId}`).then(res => res.data);
      if (!data?.error) {
        return data.data;
      }
    } catch (error: any) {
      console.log('Error:', error);
    }
  };

  return {
    fetchGroupChats,
    formatPayload,
    fetchGroupChatById,
  };
}
